import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import Header from "@/components/Header";
import api from "@/utils/api";
import {
  Bell, CheckCircle2, XCircle, Package, AlertTriangle,
  Search, Clock, CheckCheck
} from "lucide-react";

const TYPE_META = {
  INSPECTION_REQUIRED: { icon: Search, color: "bg-blue-100 text-blue-600", label: "Inspection Required" },
  INSPECTION_FAIL:     { icon: XCircle, color: "bg-red-100 text-red-600", label: "Inspection Failed" },
  RM_RECEIVED:         { icon: Package, color: "bg-green-100 text-green-600", label: "RM Received" },
  LOW_STOCK:           { icon: AlertTriangle, color: "bg-amber-100 text-amber-600", label: "Low Stock" },
  JOB_OVERDUE:         { icon: Clock, color: "bg-orange-100 text-orange-600", label: "Job Overdue" },
  CHECKSHEET_OVERDUE:  { icon: AlertTriangle, color: "bg-red-100 text-red-600", label: "Checksheet Overdue" },
};

const Notifications = () => {
  const navigate = useNavigate();
  const [notifications, setNotifications] = useState([]);
  const [loading, setLoading] = useState(true);

  const fetchNotifs = () => {
    setLoading(true);
    api.get("/notifications")
      .then((r) => setNotifications(r.data))
      .catch(() => {})
      .finally(() => setLoading(false));
  };

  useEffect(() => { fetchNotifs(); }, []);

  const markRead = async (id) => {
    await api.post(`/notifications/mark-read/${id}`).catch(() => {});
    setNotifications((prev) =>
      prev.map((n) => n.notification_id === id ? { ...n, is_read: true } : n)
    );
  };

  const markAllRead = async () => {
    await api.post("/notifications/mark-all-read").catch(() => {});
    setNotifications((prev) => prev.map((n) => ({ ...n, is_read: true })));
  };

  const unreadCount = notifications.filter((n) => !n.is_read).length;

  const formatTime = (ts) => {
    if (!ts) return "";
    try {
      const d = new Date(ts);
      return d.toLocaleString("en-IN", { day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit" });
    } catch {
      return ts.slice(0, 16);
    }
  };

  return (
    <div className="min-h-screen bg-slate-50 pb-4">
      <Header title="Notifications" showBack backPath="/home" />

      <div className="max-w-md mx-auto px-4 py-4">
        {/* Header bar */}
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-2">
            <Bell size={18} className="text-[#1A3560]" />
            <span className="font-extrabold text-slate-900">
              Notifications
              {unreadCount > 0 && (
                <span className="ml-2 text-xs bg-[#DC2626] text-white px-2 py-0.5 rounded-full font-bold">
                  {unreadCount} new
                </span>
              )}
            </span>
          </div>
          {unreadCount > 0 && (
            <button
              data-testid="mark-all-read-btn"
              onClick={markAllRead}
              className="flex items-center gap-1.5 text-xs text-[#1A3560] font-bold hover:text-blue-800 transition-all"
            >
              <CheckCheck size={14} /> Mark all read
            </button>
          )}
        </div>

        {loading ? (
          <div className="text-center py-12 text-slate-400">Loading...</div>
        ) : notifications.length === 0 ? (
          <div className="bg-white rounded-2xl border border-slate-200 p-10 text-center">
            <Bell size={40} className="mx-auto mb-3 text-slate-200" />
            <p className="font-bold text-slate-700">No notifications yet</p>
            <p className="text-slate-400 text-sm mt-1">कोई सूचना नहीं</p>
          </div>
        ) : (
          <div className="space-y-2">
            {notifications.map((notif) => {
              const meta = TYPE_META[notif.type] || { icon: Bell, color: "bg-slate-100 text-slate-500", label: notif.type };
              const Icon = meta.icon;
              return (
                <button
                  key={notif.notification_id}
                  data-testid={`notif-${notif.notification_id}`}
                  onClick={() => !notif.is_read && markRead(notif.notification_id)}
                  className={`w-full text-left rounded-2xl border-2 p-4 transition-all ${
                    notif.is_read
                      ? "bg-white border-slate-100 opacity-70"
                      : "bg-white border-[#1A3560] shadow-sm"
                  }`}
                >
                  <div className="flex items-start gap-3">
                    <div className={`w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0 ${meta.color}`}>
                      <Icon size={18} />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-start justify-between gap-2">
                        <p className={`text-sm font-bold ${notif.is_read ? "text-slate-600" : "text-slate-900"}`}>
                          {notif.title}
                        </p>
                        {!notif.is_read && (
                          <div className="w-2.5 h-2.5 bg-[#1A3560] rounded-full flex-shrink-0 mt-1" />
                        )}
                      </div>
                      <p className="text-xs text-slate-500 mt-0.5 line-clamp-2">{notif.body}</p>
                      <p className="text-xs text-slate-400 mt-1.5">{formatTime(notif.created_at)}</p>
                    </div>
                  </div>
                </button>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
};

export default Notifications;
